import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_AUDIT_LOGS } from '../graphql/operations';
import './AdminShared.css';
import './AuditDashboard.css';

const AGENT_OPTIONS = [
  { value: '', label: 'All Agents' },
  { value: 'sherlock', label: 'Sherlock' },
  { value: 'bedrock-chat', label: 'Bedrock Chat' },
  // bedrock-voice rows arrive once the voice-agent AgentCore container
  // (KAP-326 / C1) deploys to dev-bedrock and a real PSTN call lands.
  // Voice tool dispatch writes audit rows via
  // `src/functions/voice-agent/app/VoiceAgent/audit.py`, mirroring the
  // chat-agent pattern. Per ADR-0046 §6.4.2 closing the pre-existing
  // voice Activity Ledger visibility gap.
  { value: 'bedrock-voice', label: 'Bedrock Voice' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'error', label: 'Error' },
  { value: 'pending', label: 'Pending Confirmation' },
  { value: 'canceled', label: 'Canceled' }
];

const CONFIRMATION_OPTIONS = [
  { value: '', label: 'All Confirmation States' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'not-required', label: 'Not required' }
];

const OPERATION_TYPE_OPTIONS = [
  { value: '', label: 'All Operation Types' },
  { value: 'RETRIEVE', label: 'Retrieve' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'CREATE', label: 'Create' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'OTHER', label: 'Other' }
];

const CONVERSATION_TYPE_OPTIONS = [
  { value: '', label: 'All Conversation Types' },
  { value: 'CHANNEL', label: 'Channel' },
  { value: 'DM', label: 'Direct Message' },
  { value: 'AI', label: 'AI Assistant' }
];

const EXECUTION_PHASE_OPTIONS = [
  { value: '', label: 'All Execution Phases' },
  { value: 'primary', label: 'Primary' },
  { value: 'helper', label: 'Helper' },
  { value: 'post_action_fetch', label: 'Post Action Fetch' }
];

const QUICK_LIFECYCLE_CHIPS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'complete', label: 'Complete' },
  { value: 'error', label: 'Error' },
  { value: 'canceled', label: 'Canceled' }
];

function getInitials(name) {
  if (name == null || name === '') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function relativeTime(timestamp) {
  if (timestamp == null || timestamp === '') return '';
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString();
}

function statusColor(status) {
  if (status === 'complete') return 'green';
  if (status === 'confirmed') return 'blue';
  if (status === 'success') return 'green';
  if (status === 'error') return 'red';
  return 'amber';
}

function statusLabel(status) {
  if (status == null || status === '') return 'unknown';
  return status
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatTimestamp(ts) {
  if (ts == null || ts === '') return '';
  const d = new Date(ts);
  return d.toLocaleString();
}

function formatDuration(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatModifiedData(value) {
  if (value == null || value === '') return 'N/A';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (_error) {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
}

function lifecycleStatus(entry) {
  const confirmation = String(entry?.confirmationStatus || 'not-required').trim().toLowerCase();
  const result = String(entry?.resultStatus || 'unknown').trim().toLowerCase();

  if (result === 'pending' || confirmation === 'pending') return 'pending';
  if (result === 'canceled' || confirmation === 'canceled') return 'canceled';
  if (result === 'error') return 'error';
  if (confirmation === 'confirmed' && result === 'success') return 'complete';
  if (confirmation === 'confirmed') return 'confirmed';
  if (result === 'success') return 'success';
  return result || 'unknown';
}

function buildDistinctValues(entries, selector) {
  const values = new Set();
  entries.forEach((entry) => {
    const rawValue = selector(entry);
    const normalized = String(rawValue || '').trim();
    if (normalized) values.add(normalized);
  });
  return [...values].sort((left, right) => left.localeCompare(right));
}

function AuditDashboard() {
  const [filters, setFilters] = useState({
    username: '',
    action: '',
    agent: '',
    toolName: '',
    status: '',
    operationType: '',
    capabilityDomain: '',
    conversationType: '',
    executionPhase: '',
    sourceMessageId: '',
    confirmationStatus: '',
    lifecycleStatus: '',
    startDate: '',
    endDate: ''
  });
  const [debouncedUsername, setDebouncedUsername] = useState('');
  const [cursor, setCursor] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (selectedEntry == null) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedEntry(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntry]);

  // Debounce username filter (500ms)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedUsername(filters.username);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [filters.username]);

  const queryFilter = {};
  if (debouncedUsername !== '') queryFilter.username = debouncedUsername;
  if (filters.action !== '') queryFilter.action = filters.action;
  if (filters.agent !== '') queryFilter.agent = filters.agent;
  if (filters.toolName !== '') queryFilter.toolName = filters.toolName;
  if (filters.status !== '') queryFilter.status = filters.status;
  if (filters.operationType !== '') queryFilter.operationType = filters.operationType;
  if (filters.capabilityDomain !== '') queryFilter.capabilityDomain = filters.capabilityDomain;
  if (filters.conversationType !== '') queryFilter.conversationType = filters.conversationType;
  if (filters.executionPhase !== '') queryFilter.executionPhase = filters.executionPhase;
  if (filters.sourceMessageId !== '') queryFilter.sourceMessageId = filters.sourceMessageId;
  if (filters.startDate !== '') queryFilter.startDate = new Date(filters.startDate).toISOString();
  if (filters.endDate !== '') queryFilter.endDate = new Date(filters.endDate).toISOString();

  const { loading, error, data, fetchMore } = useQuery(LIST_AUDIT_LOGS, {
    variables: {
      filter: Object.keys(queryFilter).length > 0 ? queryFilter : undefined,
      limit: 50
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    setAllItems(data?.listAuditLogs?.items || []);
    setCursor(data?.listAuditLogs?.nextCursor || null);
  }, [data]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    // Only clear items immediately for non-text filters (dropdowns, dates)
    if (field === 'username') return;
    setAllItems([]);
    setCursor(null);
  };

  const handleLoadMore = async () => {
    if (cursor == null || cursor === '') return;
    const result = await fetchMore({
      variables: { cursor }
    });
    const newItems = result?.data?.listAuditLogs?.items || [];
    setAllItems((prev) => [...prev, ...newItems]);
    setCursor(result?.data?.listAuditLogs?.nextCursor || null);
  };

  const rawItems = useMemo(
    () => (allItems.length > 0 ? allItems : (data?.listAuditLogs?.items || [])),
    [allItems, data]
  );
  const actionSuggestions = useMemo(
    () => buildDistinctValues(rawItems, (entry) => entry.action || entry.workflowName),
    [rawItems]
  );
  const capabilitySuggestions = useMemo(
    () => buildDistinctValues(rawItems, (entry) => entry.capabilityDomain),
    [rawItems]
  );
  const executionPhaseOptions = useMemo(() => {
    const dynamicPhases = buildDistinctValues(rawItems, (entry) => entry.executionPhase)
      .filter((phase) => !EXECUTION_PHASE_OPTIONS.some((option) => option.value === phase));
    return [
      ...EXECUTION_PHASE_OPTIONS,
      ...dynamicPhases.map((phase) => ({
        value: phase,
        label: statusLabel(phase)
      }))
    ];
  }, [rawItems]);

  const filteredItems = rawItems.filter((entry) => {
    if (filters.confirmationStatus && entry?.confirmationStatus !== filters.confirmationStatus) {
      return false;
    }
    if (filters.lifecycleStatus && lifecycleStatus(entry) !== filters.lifecycleStatus) {
      return false;
    }
    return true;
  });
  const items = [...filteredItems].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const activeFilterCount = useMemo(() => {
    const {
      username,
      action,
      agent,
      toolName,
      status,
      operationType,
      capabilityDomain,
      conversationType,
      executionPhase,
      sourceMessageId,
      confirmationStatus,
      lifecycleStatus,
      startDate,
      endDate
    } = filters;
    return [
      username,
      action,
      agent,
      toolName,
      status,
      operationType,
      capabilityDomain,
      conversationType,
      executionPhase,
      sourceMessageId,
      confirmationStatus,
      lifecycleStatus,
      startDate,
      endDate
    ]
      .filter((value) => Boolean(value && String(value).trim()))
      .length;
  }, [filters]);
  const statusCounts = useMemo(() => {
    return items.reduce((acc, entry) => {
      const key = entry?.resultStatus || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [items]);
  const lifecycleCounts = useMemo(() => {
    return items.reduce((acc, entry) => {
      const key = lifecycleStatus(entry);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [items]);
  const contributorGroups = useMemo(() => {
    const summarizeTop = (selectLabel) => {
      const counts = new Map();
      items.forEach((entry) => {
        const rawLabel = selectLabel(entry);
        const label = rawLabel && String(rawLabel).trim() ? String(rawLabel).trim() : 'Unspecified';
        counts.set(label, (counts.get(label) || 0) + 1);
      });
      return [...counts.entries()]
        .sort((left, right) => right[1] - left[1])
        .slice(0, 4)
        .map(([label, count]) => ({ label, count }));
    };

    return [
      { id: 'workflow', title: 'Top Workflows', rows: summarizeTop((entry) => entry.workflowName || entry.action) },
      { id: 'agent', title: 'Top Agents', rows: summarizeTop((entry) => entry.agent) },
      { id: 'tool', title: 'Top Tools', rows: summarizeTop((entry) => entry.toolName) }
    ];
  }, [items]);
  const timelineGroups = useMemo(() => {
    const sortedAsc = [...items].sort((left, right) => new Date(left.timestamp) - new Date(right.timestamp));
    const groups = new Map();

    sortedAsc.forEach((entry) => {
      const conversationId = String(entry.conversationId || 'none');
      const workflowName = String(entry.workflowName || entry.action || 'unknown');
      const toolName = String(entry.toolName || 'unknown_tool');
      const requestSummary = String(entry.requestSummary || entry.action || 'Untitled request');
      const key = `${conversationId}::${workflowName}::${toolName}::${requestSummary}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          conversationId,
          workflowName,
          toolName,
          requestSummary,
          latestTimestamp: entry.timestamp,
          events: []
        });
      }

      const group = groups.get(key);
      group.events.push(entry);
      if (new Date(entry.timestamp) > new Date(group.latestTimestamp)) {
        group.latestTimestamp = entry.timestamp;
      }
    });

    return [...groups.values()]
      .map((group) => {
        const checkpoints = [];
        group.events.forEach((entry) => {
          const step = lifecycleStatus(entry);
          if (checkpoints.length === 0 || checkpoints[checkpoints.length - 1].status !== step) {
            checkpoints.push({
              status: step,
              timestamp: entry.timestamp,
              auditId: entry.auditId
            });
          }
        });
        return {
          ...group,
          checkpoints
        };
      })
      .sort((left, right) => new Date(right.latestTimestamp) - new Date(left.latestTimestamp))
      .slice(0, 12);
  }, [items]);
  const isInitialLoad = loading && items.length === 0;

  if (isInitialLoad) {
    return <div className="ad-state">Loading audit logs...</div>;
  }

  if (error && items.length === 0) {
    return <div className="ad-state ad-error">Error: {error.message}</div>;
  }

  const handleClearFilters = () => {
    setFilters({
      username: '',
      action: '',
      agent: '',
      toolName: '',
      status: '',
      operationType: '',
      capabilityDomain: '',
      conversationType: '',
      executionPhase: '',
      sourceMessageId: '',
      confirmationStatus: '',
      lifecycleStatus: '',
      startDate: '',
      endDate: ''
    });
    setDebouncedUsername('');
  };

  return (
    <section className="ad">
      <header className="admin-view-header">
        <div>
          <h2>Activity Ledger</h2>
          <p>View employee requests, agent actions, and governed workflow outcomes.</p>
        </div>
      </header>

      <div className="admin-filters">
        <div className="ad-filter-toolbar">
          <div className="ad-filter-summary">
            <span className="admin-pill admin-pill-sm admin-pill-gray">{activeFilterCount} active filters</span>
            <span className="ad-filter-summary-text">Quick status chips and clear/reset support all filters.</span>
          </div>
          <div className="ad-filter-actions">
            <div className="ad-status-chips" role="group" aria-label="Quick status filters">
              {QUICK_LIFECYCLE_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              className={`ad-status-chip ${filters.lifecycleStatus === chip.value ? 'is-active' : ''}`}
              onClick={() => handleFilterChange('lifecycleStatus', chip.value)}
              aria-pressed={filters.lifecycleStatus === chip.value}
            >
              {chip.label}
            </button>
              ))}
            </div>
            <button type="button" className="ad-clear-btn" onClick={handleClearFilters}>Clear filters</button>
          </div>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Username</span>
          <input
            className="admin-filter-input"
            type="text"
            placeholder="jane@kapsa.ai"
            value={filters.username}
            onChange={(e) => handleFilterChange('username', e.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Action</span>
          <input
            className="admin-filter-input"
            type="text"
            list="audit-action-options"
            placeholder="CLIENT_LOOKUP"
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Agent</span>
          <select
            className="admin-filter-input"
            value={filters.agent}
            onChange={(e) => handleFilterChange('agent', e.target.value)}
          >
            {AGENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Tool</span>
          <input
            className="admin-filter-input"
            type="text"
            placeholder="lookup_client"
            value={filters.toolName}
            onChange={(e) => handleFilterChange('toolName', e.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Status</span>
          <select
            className="admin-filter-input"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Operation Type</span>
          <select
            className="admin-filter-input"
            value={filters.operationType}
            onChange={(e) => handleFilterChange('operationType', e.target.value)}
          >
            {OPERATION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Capability Domain</span>
          <input
            className="admin-filter-input"
            type="text"
            list="audit-capability-options"
            placeholder="patient"
            value={filters.capabilityDomain}
            onChange={(e) => handleFilterChange('capabilityDomain', e.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Conversation Type</span>
          <select
            className="admin-filter-input"
            value={filters.conversationType}
            onChange={(e) => handleFilterChange('conversationType', e.target.value)}
          >
            {CONVERSATION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Execution Phase</span>
          <select
            className="admin-filter-input"
            value={filters.executionPhase}
            onChange={(e) => handleFilterChange('executionPhase', e.target.value)}
          >
            {executionPhaseOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Source Correlation</span>
          <input
            className="admin-filter-input"
            type="text"
            placeholder="message or request key"
            value={filters.sourceMessageId}
            onChange={(e) => handleFilterChange('sourceMessageId', e.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Confirmation</span>
          <select
            className="admin-filter-input"
            value={filters.confirmationStatus}
            onChange={(e) => handleFilterChange('confirmationStatus', e.target.value)}
          >
            {CONFIRMATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">From</span>
          <input
            className="admin-filter-input"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">To</span>
          <input
            className="admin-filter-input"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
        <datalist id="audit-action-options">
          {actionSuggestions.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        <datalist id="audit-capability-options">
          {capabilitySuggestions.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      </div>

      {items.length === 0 ? (
        <div className="ad-empty">No audit entries found.</div>
      ) : (
        <>
          <section className="al-summary-row" aria-label="Activity summary">
            <article className="al-summary-card">
              <span>Total events</span>
              <strong>{items.length}</strong>
            </article>
            <article className="al-summary-card">
              <span>Pending</span>
              <strong>{lifecycleCounts.pending || 0}</strong>
            </article>
            <article className="al-summary-card">
              <span>Confirmed</span>
              <strong>{lifecycleCounts.confirmed || 0}</strong>
            </article>
            <article className="al-summary-card">
              <span>Complete</span>
              <strong>{lifecycleCounts.complete || 0}</strong>
            </article>
            <article className="al-summary-card">
              <span>Error</span>
              <strong>{statusCounts.error || 0}</strong>
            </article>
          </section>
          <section className="al-contributor-row" aria-label="Activity contributors">
            {contributorGroups.map((group) => (
              <article className="al-contributor-card" key={group.id}>
                <h3>{group.title}</h3>
                <div className="al-contributor-pills">
                  {group.rows.length === 0 ? (
                    <span className="admin-pill admin-pill-sm admin-pill-gray">No activity</span>
                  ) : (
                    group.rows.map((row) => (
                      <span className="admin-pill admin-pill-sm admin-pill-gray" key={`${group.id}-${row.label}`}>
                        {row.label} · {row.count}
                      </span>
                    ))
                  )}
                </div>
              </article>
            ))}
          </section>
          <section className="al-contributor-row" aria-label="Grouped lifecycle timeline">
            {timelineGroups.length === 0 ? (
              <article className="al-contributor-card">
                <h3>Grouped Timeline</h3>
                <div className="al-contributor-pills">
                  <span className="admin-pill admin-pill-sm admin-pill-gray">No grouped lifecycle data</span>
                </div>
              </article>
            ) : (
              timelineGroups.map((group) => (
                <article className="al-contributor-card" key={group.key}>
                  <h3>{group.requestSummary}</h3>
                  <div className="al-request-sub">
                    {group.workflowName} · {group.toolName} · conv {group.conversationId.slice(0, 10)}
                  </div>
                  <div className="al-contributor-pills">
                    {group.checkpoints.map((checkpoint, index) => (
                      <React.Fragment key={`${group.key}-${index}-${checkpoint.status}`}>
                        <span className={`admin-pill admin-pill-sm admin-pill-${statusColor(checkpoint.status)}`}>
                          {statusLabel(checkpoint.status)}
                        </span>
                        {index < group.checkpoints.length - 1 ? (
                          <span className="admin-pill admin-pill-sm admin-pill-gray">→</span>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                </article>
              ))
            )}
          </section>
          <div className="admin-results-count">
            Showing {items.length} events{cursor ? ' (more available)' : ''}
          </div>
          <div className="admin-card-list">
            {items.map((entry) => (
              <div
                key={entry.auditId}
                className="admin-card-row"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedEntry(entry)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedEntry(entry);
                  }
                }}
              >
                <div className="al-row-content">
                  <div className="al-user">
                    <div
                      className="admin-avatar"
                      style={{ width: 32, height: 32, background: 'var(--primary)', fontSize: 12 }}
                    >
                      {getInitials(entry.displayName || entry.username)}
                    </div>
                    <span className="al-user-name">{entry.displayName || entry.username}</span>
                  </div>
                  <div className="al-request">
                    <div className="al-request-text">{entry.requestSummary || entry.action}</div>
                    <div className="al-request-sub">
                      {entry.agent} · {entry.action}
                      {entry.durationMs ? ` · ${formatDuration(entry.durationMs)}` : ''}
                    </div>
                    <div className="al-request-tags">
                      <span className="admin-pill admin-pill-sm admin-pill-gray">{entry.workflowName || entry.action}</span>
                      <span className="admin-pill admin-pill-sm admin-pill-gray">{entry.toolName || 'unknown tool'}</span>
                      <span className="admin-pill admin-pill-sm admin-pill-gray">{entry.operationType || 'OTHER'}</span>
                      <span className="admin-pill admin-pill-sm admin-pill-gray">{entry.capabilityDomain || 'general'}</span>
                      <span className="admin-pill admin-pill-sm admin-pill-gray">{entry.conversationType || 'DM'}</span>
                      <span className="admin-pill admin-pill-sm admin-pill-gray">{entry.executionPhase || 'primary'}</span>
                      <span className="admin-pill admin-pill-sm admin-pill-blue">{entry.confirmationStatus || 'not-required'}</span>
                      <span className={`admin-pill admin-pill-sm admin-pill-${statusColor(lifecycleStatus(entry))}`}>
                        {statusLabel(lifecycleStatus(entry))}
                      </span>
                    </div>
                  </div>
                  <div className="al-meta">
                    <span className={`admin-pill admin-pill-sm admin-pill-${statusColor(entry.resultStatus)}`}>
                      {statusLabel(entry.resultStatus)}
                    </span>
                    <button
                      type="button"
                      className="al-detail-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedEntry(entry);
                      }}
                    >
                      Details
                    </button>
                    <span className="al-time" title={formatTimestamp(entry.timestamp)}>
                      {relativeTime(entry.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {cursor && (
        <div className="admin-load-more">
          <button className="admin-load-more-btn" type="button" onClick={handleLoadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {selectedEntry && (
        <div className="ad-modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div
            className="ad-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="audit-entry-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ad-modal-header">
              <h3 id="audit-entry-title">{selectedEntry.displayName || selectedEntry.username}</h3>
              <button
                type="button"
                className="ad-modal-close"
                onClick={() => setSelectedEntry(null)}
                aria-label="Close audit entry details"
              >
                &times;
              </button>
            </div>
            <div className="ad-modal-subtitle">{selectedEntry.action}</div>
            <div className="ad-modal-body">
              <dl className="ad-detail-list">
                <dt>Employee</dt><dd>{selectedEntry.displayName || selectedEntry.username} ({selectedEntry.username})</dd>
                <dt>Request</dt><dd>{selectedEntry.requestSummary || 'N/A'}</dd>
                <dt>Agent</dt><dd>{selectedEntry.agent}</dd>
                <dt>Workflow</dt><dd>{selectedEntry.workflowName || selectedEntry.action}</dd>
                <dt>Tool</dt><dd>{selectedEntry.toolName}</dd>
                <dt>Operation Type</dt><dd>{selectedEntry.operationType || 'OTHER'}</dd>
                <dt>Capability Domain</dt><dd>{selectedEntry.capabilityDomain || 'general'}</dd>
                <dt>Conversation Type</dt><dd>{selectedEntry.conversationType || 'DM'}</dd>
                <dt>Execution Phase</dt><dd>{selectedEntry.executionPhase || 'primary'}</dd>
                <dt>Source Correlation</dt><dd>{selectedEntry.sourceMessageId || 'N/A'}</dd>
                <dt>Confirmation</dt><dd>{selectedEntry.confirmationStatus || 'not-required'}</dd>
                <dt>Lifecycle</dt><dd>{statusLabel(lifecycleStatus(selectedEntry))}</dd>
                <dt>Result</dt><dd>{selectedEntry.resultStatus}</dd>
                <dt>Result Summary</dt><dd>{selectedEntry.resultSummary || 'N/A'}</dd>
                <dt>Modified Data</dt>
                <dd>
                  <pre className="ad-modified-data">{formatModifiedData(selectedEntry.modifiedData)}</pre>
                </dd>
                <dt>Timestamp</dt><dd>{formatTimestamp(selectedEntry.timestamp)}</dd>
                <dt>Duration</dt><dd>{formatDuration(selectedEntry.durationMs)}</dd>
                <dt>Conversation ID</dt><dd>{selectedEntry.conversationId || 'N/A'}</dd>
              </dl>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default AuditDashboard;
